import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";

import {
  fetchCreditDashboard,
  type CreditDashboardConfirmedAmount,
  type CreditDashboardCurrentAmount,
  type CreditDashboardDocumentAmount,
  type CreditDashboardFixedPeriodKey,
  type CreditDashboardPeriodKey,
  type CreditDashboardProposal,
  type CreditDashboardResponse,
} from "../api/belege";
import { isDemoMode } from "../lib/env";
import { formatDateShort, toNumber } from "../lib/format";
import AccountingPeriodControl from "./AccountingPeriodControl";
import { accountingRangeLabel } from "../lib/accountingPeriod";

import { usePersonalView } from "../lib/usePersonalView";
import PersonalViewNotice from "./PersonalViewNotice";

type CreditDashboardProps = {
  data: CreditDashboardResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /**
   * In der Belegliste trägt die Seite die Zeitraumwahl, und beide lesen
   * dieselben Adressparameter (#539). Eine zweite wäre nur ein zweiter Weg,
   * dasselbe zu sagen.
   */
  ownPeriodControl?: boolean;
};

const MAX_CUSTOM_PERIOD_DAYS = 3660;

export default function CreditDashboard({
  data,
  isLoading,
  isError,
  onRetry,
  ownPeriodControl = true,
}: CreditDashboardProps) {
  const demo = isDemoMode();
  const queryClient = useQueryClient();
  const personalView = usePersonalView("credits");
  const { params: searchParams, change: setSearchParams } = personalView;
  const fromParam = searchParams.get("from") ?? "";
  const toParam = searchParams.get("to") ?? "";
  const periodKey: CreditDashboardPeriodKey = searchParams.has("from") || searchParams.has("to") || searchParams.get("period") === "custom" ? "custom" : searchParams.get("period") === "1y" ? "1y" : "1m";
  const [customRangeRequested, setCustomRangeOpen] = useState(periodKey === "custom");
  // Den eigenen Zeitraum kann auch die Seite setzen, in der dieses Dashboard
  // steht (#539): dann sagt ihn die Adresse, nicht der Klick hier.
  const customRangeOpen = customRangeRequested || periodKey === "custom";
  const [customFrom, setCustomFrom] = useState(fromParam);
  const [customTo, setCustomTo] = useState(toParam);
  useEffect(() => {
    if (periodKey === "custom") { setCustomFrom(fromParam); setCustomTo(toParam); }
    setCustomRangeOpen(periodKey === "custom");
  }, [fromParam, toParam, periodKey]);
  const customRange = useMemo(() => periodKey === "custom" && data && fromParam && toParam && !validateCustomRange(fromParam, toParam, data.as_of_date)
    ? { from: fromParam, to: toParam } : null, [periodKey, fromParam, toParam, data]);
  /**
   * Der gewählte Zeitraum steht in der Adresse, sobald er gewählt ist - genau
   * wie in der Belegliste, in der dieses Dashboard stehen kann (#539). Nur ein
   * gültiger eigener Zeitraum wird als persönliche Ansicht gemerkt; ein noch
   * unvollständiger bleibt sichtbar, ohne sich zu merken.
   */
  function savePeriod(period: CreditDashboardPeriodKey, from = "", to = "", persist = true) {
    const next = new URLSearchParams(searchParams);
    next.set("period", period);
    next.delete("from"); next.delete("to");
    if (period === "custom") { next.set("from", from); next.set("to", to); }
    if (persist && period === "custom" && data && validateCustomRange(from, to, data.as_of_date)) personalView.setUrl(next);
    else setSearchParams(next);
  }
  const customQuery = useQuery({
    queryKey: [
      "belege",
      "credits",
      "custom",
      customRange?.from ?? "",
      customRange?.to ?? "",
    ],
    queryFn: () => fetchCreditDashboard(customRange ?? undefined),
    enabled: personalView.ready && customRange !== null,
    retry: false,
    staleTime: 60_000,
  });
  useEffect(() => {
    const response = customQuery.data;
    const responsePeriod = response?.periods.custom;
    if (
      !customRange ||
      !response ||
      responsePeriod?.from_date !== customRange.from ||
      responsePeriod.to_date !== customRange.to
    ) {
      return;
    }
    // Only the response observed under the active range key may refresh the
    // period-independent base snapshot. A slower superseded request remains
    // cached under its own key and can never roll the visible base state back.
    queryClient.setQueryData<CreditDashboardResponse>(
      ["belege", "credits"],
      withoutCustomPeriod(response),
    );
  }, [customQuery.data, customRange, queryClient]);
  const baseData = data ? withoutCustomPeriod(data) : undefined;
  const customRangeMatchesDraft = Boolean(
    customRange &&
      customFrom === customRange.from &&
      customTo === customRange.to,
  );
  const customPeriod =
    customRangeMatchesDraft &&
    customRange &&
    customQuery.data?.periods.custom?.from_date === customRange.from &&
    customQuery.data.periods.custom.to_date === customRange.to
      ? customQuery.data.periods.custom
      : undefined;
  const activeData =
    baseData && periodKey === "custom" && customPeriod
      ? {
          ...baseData,
          periods: {
            ...baseData.periods,
            custom: customPeriod,
          },
        }
      : baseData;
  const customValidation = data
    ? validateCustomRange(customFrom, customTo, data.as_of_date)
    : null;

  function selectFixedPeriod(period: CreditDashboardFixedPeriodKey) {
    savePeriod(period);
    setCustomRangeOpen(false);
  }

  function openCustomRange() {
    const seed = data?.periods[periodKey] ?? data?.periods["1m"];
    const from = customFrom || seed?.from_date || "";
    const to = customTo || seed?.to_date || "";
    setCustomFrom(from); setCustomTo(to); setCustomRangeOpen(true);
    // Der bisher gezeigte Zeitraum wird zum eigenen: dieselben Daten, nur jetzt
    // in der Adresse und damit teilbar.
    savePeriod("custom", from, to, false);
  }

  function updateCustomRange(nextFrom: string, nextTo: string) {
    if (nextFrom === customFrom && nextTo === customTo) return;
    setCustomFrom(nextFrom); setCustomTo(nextTo);
    savePeriod("custom", nextFrom, nextTo);
  }

  return (
    <section
      aria-label="Gutschriftenstatus"
      className="min-w-0"
      data-testid="credit-dashboard"
    >
      {ownPeriodControl ? (
        <PersonalViewNotice withReset view={personalView} defaultLabel="Gutschrift-Belege: 1 Monat; Erwartungen: alle Zeiträume" />
      ) : null}
      {activeData ? (
        <div className="mb-3 text-right text-xs text-slate-500">
          Stand {formatDateShort(activeData.as_of_date)}
        </div>
      ) : null}

      {!personalView.ready || (isLoading && !activeData) ? (
        <CreditDashboardLoading />
      ) : !activeData ? (
        <div className="rounded-lg border border-rose-100 bg-rose-50 px-4 py-5">
          <p role="alert" className="text-sm text-rose-700">
            Gutschriften-Übersicht konnte nicht geladen werden.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Erneut laden
          </button>
        </div>
      ) : (
        <CreditDashboardContent
          data={activeData}
          demo={demo}
          ownPeriodControl={ownPeriodControl}
          periodKey={periodKey}
          onFixedPeriodChange={selectFixedPeriod}
          onCustomRangeOpen={openCustomRange}
          customRangeOpen={customRangeOpen}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFromChange={(value) => updateCustomRange(value, customTo)}
          onCustomToChange={(value) => updateCustomRange(customFrom, value)}
          customValidation={customValidation}
          customPeriodLoading={
            periodKey === "custom" &&
            customRange !== null &&
            customRangeMatchesDraft &&
            customQuery.isPending
          }
          customPeriodUpdating={
            periodKey === "custom" &&
            customRangeMatchesDraft &&
            customQuery.isFetching
          }
          customPeriodError={
            periodKey === "custom" &&
            customRangeMatchesDraft &&
            customQuery.isError
          }
          onCustomRetry={() => {
            void customQuery.refetch();
          }}
        />
      )}

      {activeData && isError ? (
        <p role="alert" className="mt-3 text-xs text-rose-700">
          Der vorhandene Stand konnte nicht aktualisiert werden.
        </p>
      ) : null}
    </section>
  );
}

function withoutCustomPeriod(
  data: CreditDashboardResponse,
): CreditDashboardResponse {
  const { custom: _custom, ...fixedPeriods } = data.periods;
  return { ...data, periods: fixedPeriods };
}

function CreditDashboardLoading() {
  return (
    <div
      aria-label="Gutschriften-Übersicht wird geladen"
      className="grid gap-3 lg:grid-cols-2"
    >
      <div className="h-28 animate-pulse rounded-lg bg-slate-100" />
      <div className="h-28 animate-pulse rounded-lg bg-slate-100" />
    </div>
  );
}

function CreditPeriodLoading() {
  return (
    <div
      aria-label="Eigener Zeitraum wird geladen"
      className="mt-3 space-y-3"
    >
      <div className="h-12 animate-pulse rounded-md bg-slate-100" />
      <div className="h-16 animate-pulse rounded-md bg-slate-100" />
    </div>
  );
}

function CreditDashboardContent({
  data,
  demo,
  ownPeriodControl,
  periodKey,
  onFixedPeriodChange,
  onCustomRangeOpen,
  customRangeOpen,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  customValidation,
  customPeriodLoading,
  customPeriodUpdating,
  customPeriodError,
  onCustomRetry,
}: {
  data: CreditDashboardResponse;
  demo: boolean;
  periodKey: CreditDashboardPeriodKey;
  ownPeriodControl: boolean;
  onFixedPeriodChange: (period: CreditDashboardFixedPeriodKey) => void;
  onCustomRangeOpen: () => void;
  customRangeOpen: boolean;
  customFrom: string;
  customTo: string;
  onCustomFromChange: (value: string) => void;
  onCustomToChange: (value: string) => void;
  customValidation: string | null;
  customPeriodLoading: boolean;
  customPeriodUpdating: boolean;
  customPeriodError: boolean;
  onCustomRetry: () => void;
}) {
  const location = useLocation();
  const fromPath = `${location.pathname}${location.search}`;
  const period = data.periods[periodKey];
  const customPeriodBusy = customPeriodLoading || customPeriodUpdating;
  const matchingActive = data.matching.status === "active";
  const matchingHasDataIssue = data.matching.status === "data_quality_issue";
  const proposalCount = data.current.proposal_count ?? 0;

  return (
    <>
      {data.data_quality.status === "review_required" ? (
        <DataQualityNotice data={data} />
      ) : null}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <section
          aria-labelledby="credit-expectations-title"
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2
                id="credit-expectations-title"
                className="text-sm font-semibold text-slate-900"
              >
                Aktueller Erwartungsbestand
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Offene Preisprüfungen mit „Gutschrift erwartet“ · alle Zeiträume.
              </p>
            </div>
            <Link
              to="/rechnungspruefung?review=credit_expected"
              className="text-sm font-medium text-emerald-700 hover:underline"
            >
              Erwartungen öffnen →
            </Link>
          </div>

          {data.current.expected_position_count === 0 ? (
            <div className="mt-5 rounded-md border border-slate-200 bg-white px-3 py-4 text-sm text-slate-600">
              Aktuell sind keine Gutschriften erwartet.
            </div>
          ) : (
            <>
              <div className="mt-4">
                <div className="text-2xl font-semibold tabular-nums text-slate-900">
                  {formatInteger(data.current.expected_position_count)}
                </div>
                <div className="text-sm text-slate-500">
                  {data.current.expected_position_count === 1
                    ? "erwartete Position"
                    : "erwartete Positionen"}
                </div>
              </div>
              <AmountList
                className="mt-4"
                title={
                  matchingActive
                    ? "Offener Differenzbetrag"
                    : "Erwarteter Differenzbetrag"
                }
                amounts={data.current.amounts}
                value={(amount) =>
                  matchingActive
                    ? amount.open_difference_amount
                    : amount.expected_amount
                }
              />
              {matchingActive ? (
                <>
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <SmallMetric
                      label="Teilweise gedeckt"
                      value={data.current.partial_position_count ?? 0}
                    />
                    <SmallMetric
                      label="Vorschläge prüfen"
                      value={proposalCount}
                      href={proposalCount > 0 ? "#credit-proposals" : undefined}
                    />
                  </div>
                  <AmountList
                    className="mt-4 border-t border-slate-200 pt-3"
                    title="Davon bestätigt zugeordnet"
                    amounts={data.current.amounts.filter(
                      (amount) =>
                        (toNumber(amount.confirmed_assignment_amount) ?? 0) > 0,
                    )}
                    value={(amount) => amount.confirmed_assignment_amount}
                    emptyLabel="Noch keine Teilbeträge bestätigt zugeordnet."
                  />
                </>
              ) : (
                <MatchingUnavailable
                  className="mt-4"
                  dataQualityIssue={matchingHasDataIssue}
                />
              )}
            </>
          )}
        </section>

        <section
          aria-labelledby="credit-documents-title"
          aria-busy={customPeriodBusy || undefined}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2
                id="credit-documents-title"
                className="text-sm font-semibold text-slate-900"
              >
                Gutschrift-Belege
              </h2>
            </div>
          </div>

          <div className="mt-3 border-t border-slate-100 pt-3">
            {ownPeriodControl ? (
              <AccountingPeriodControl mode={periodKey} from={customFrom} to={customTo}
                customOpen={customRangeOpen} error={customRangeOpen ? customValidation : null}
                label={period ? accountingRangeLabel(period.from_date, period.to_date) : "Zeitraum nach Belegdatum"}
                onModeChange={mode => mode === "custom" ? onCustomRangeOpen() : mode !== "all" && onFixedPeriodChange(mode)}
                onFromChange={onCustomFromChange} onToChange={onCustomToChange} />
            ) : (
              // Ohne eigene Steuerung sagt die Karte trotzdem, worauf sich ihre
              // Zahlen beziehen - die Liste darunter kann einen weiteren
              // Zeitraum zeigen als das Gutschriftenmodell kennt (#539).
              <p className="text-sm text-slate-600">
                {period
                  ? accountingRangeLabel(period.from_date, period.to_date)
                  : "Zeitraum nach Belegdatum"}
              </p>
            )}
          </div>

          {demo && (customRangeOpen || periodKey === "custom") ? (
            <p
              data-testid="credit-custom-range-demo-note"
              className="mt-3 rounded-md border border-sky-100 bg-sky-50 px-3 py-2 text-xs leading-relaxed text-sky-800"
            >
              Demo-Hinweis: Eigene Zeiträume werden aus den festen Monats- und
              Jahreswerten synthetisch verteilt und bilden keine echten
              Belegtage ab.
            </p>
          ) : null}

          {customPeriodBusy ? (
            <CreditPeriodLoading />
          ) : customPeriodError ? (
            <div className="mt-3 rounded-md border border-rose-100 bg-rose-50 px-3 py-4">
              <p role="alert" className="text-sm text-rose-700">
                Dieser Zeitraum konnte nicht geladen werden.
              </p>
              <button
                type="button"
                onClick={onCustomRetry}
                className="mt-3 inline-flex h-9 items-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Erneut laden
              </button>
            </div>
          ) : period ? (
            <>
              <div className="mt-3">
                <div className="text-2xl font-semibold tabular-nums text-slate-900">
                  {formatInteger(period.credit_documents.document_count)}
                </div>
                <div className="text-sm text-slate-500">
                  {period.credit_documents.document_count === 1
                    ? "Gutschrift-Beleg"
                    : "Gutschrift-Belege"}
                </div>
              </div>
              <AmountList
                className="mt-4"
                title="Belegsumme"
                amounts={period.credit_documents.amounts}
                value={(amount) => amount.total_amount}
                note={creditDocumentAmountNote}
                emptyLabel="Keine Gutschrift-Belege in diesem Zeitraum."
              />
              <Link
                to={`/rechnungspruefung?${new URLSearchParams({ type: "gutschrift", review: "all", from: period.from_date, to: period.to_date, return_to: fromPath })}`}
                state={{ from: fromPath }}
                className="mt-4 inline-flex min-h-9 w-full items-center justify-center rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 sm:w-auto"
              >
                Belege im Zeitraum öffnen →
              </Link>

              <div
                className={
                  period.confirmed_links?.link_count
                    ? "mt-4 rounded-md border border-emerald-100 bg-emerald-50/60 px-3 py-3"
                    : "mt-4 border-t border-slate-200 pt-3"
                }
              >
                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Derzeit bestätigt zugeordnet
                </h3>
                {period.confirmed_links ? (
                  <>
                    <p className="mt-2 text-sm text-slate-600">
                      <span className="font-semibold tabular-nums text-slate-900">
                        {formatInteger(period.confirmed_links.link_count)}
                      </span>{" "}
                      {period.confirmed_links.link_count === 1
                        ? "Zuordnung"
                        : "Zuordnungen"}{" "}
                      aus{" "}
                      <span className="font-semibold tabular-nums text-slate-900">
                        {formatInteger(
                          period.confirmed_links.credit_document_count,
                        )}
                      </span>{" "}
                      {period.confirmed_links.credit_document_count === 1
                        ? "Gutschrift-Beleg"
                        : "Gutschrift-Belegen"}
                    </p>
                    <AmountList
                      className="mt-3"
                      title="Zuordnungsbetrag"
                      amounts={period.confirmed_links.amounts}
                      value={(amount) => amount.allocation_amount}
                      emptyLabel="Keine bestätigten Beleg-Zuordnungen in diesem Zeitraum."
                    />
                  </>
                ) : (
                  <MatchingUnavailable
                    className="mt-3"
                    compact
                    dataQualityIssue={matchingHasDataIssue}
                  />
                )}
              </div>
            </>
          ) : (
            <p className="mt-3 text-sm text-slate-500">
              Ändern Sie Von oder Bis, um den eigenen Zeitraum zu laden.
            </p>
          )}
        </section>
      </div>

      {matchingActive && proposalCount > 0 ? (
        <ProposalList
          fromPath={fromPath}
          proposals={data.proposals ?? []}
          totalCount={proposalCount}
        />
      ) : null}

      <p className="mt-3 border-t border-slate-100 pt-3 text-xs leading-relaxed text-slate-500">
        Zeitraum nach Gutschrift-Belegdatum. Belegsummen und bestätigte
        Zuordnungen sind getrennt; ein manuell gesetzter Prüfstatus gilt nicht
        als bestätigte Beleg-Zuordnung.
      </p>
    </>
  );
}

type AmountRow =
  | CreditDashboardCurrentAmount
  | CreditDashboardDocumentAmount
  | CreditDashboardConfirmedAmount;

function AmountList<T extends AmountRow>({
  title,
  amounts,
  value,
  note,
  emptyLabel,
  className = "",
}: {
  title: string;
  amounts: T[];
  value: (amount: T) => string | number | null;
  note?: (amount: T) => string | null;
  emptyLabel?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="text-xs font-medium text-slate-500">{title}</div>
      {amounts.length === 0 ? (
        <p className="mt-1 text-sm text-slate-500">{emptyLabel ?? "–"}</p>
      ) : (
        <ul className="mt-1 space-y-1">
          {amounts.map((amount) => {
            const noteText = note?.(amount) ?? null;
            return (
              <li
                key={amount.currency ?? "currency-missing"}
                className="flex items-start justify-between gap-3 text-sm"
              >
                <span className="text-slate-500">
                  {amount.currency ?? "Währung offen"}
                </span>
                <span className="text-right">
                  <span className="block font-medium tabular-nums text-slate-900">
                    {formatAmount(value(amount))}
                  </span>
                  {noteText ? (
                    <span className="mt-0.5 block text-xs text-rose-700">
                      {noteText}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function SmallMetric({
  label,
  value,
  href,
}: {
  label: string;
  value: number;
  href?: string;
}) {
  const content = (
    <>
      <span className="text-xl font-semibold tabular-nums text-slate-900">
        {formatInteger(value)}
      </span>
      <span className="text-xs text-slate-500">{label}</span>
    </>
  );
  const classes =
    "flex min-w-0 flex-col rounded-md border border-slate-200 bg-white px-3 py-2";
  return href ? (
    <a href={href} className={`${classes} transition hover:border-emerald-300`}>
      {content}
    </a>
  ) : (
    <div className={classes}>{content}</div>
  );
}

function MatchingUnavailable({
  className = "",
  compact = false,
  dataQualityIssue = false,
}: {
  className?: string;
  compact?: boolean;
  dataQualityIssue?: boolean;
}) {
  return (
    <div
      className={`rounded-md border border-dashed border-slate-300 bg-white px-3 ${
        compact ? "py-2" : "py-3"
      } ${className}`}
    >
      <div className="text-sm font-medium text-slate-700">
        {dataQualityIssue
          ? "Zuordnungswerte nicht verfügbar"
          : "Zuordnung nicht aktiviert"}
      </div>
      {!compact ? (
        <p className="mt-1 text-xs leading-relaxed text-slate-500">
          {dataQualityIssue
            ? "Erwartungen und Gutschrift-Belege bleiben sichtbar. Bestätigte Zuordnungen werden erst nach Klärung der widersprüchlichen Daten wieder ausgewertet."
            : "Erwartungen und Gutschrift-Belege bleiben sichtbar; Teildeckung und Zuordnungsvorschläge sind noch nicht verfügbar."}
        </p>
      ) : null}
    </div>
  );
}

function DataQualityNotice({ data }: { data: CreditDashboardResponse }) {
  const quality = data.data_quality;
  const details: string[] = [];
  if (quality.credit_documents_without_valid_date > 0) {
    details.push(
      quality.credit_documents_without_valid_date === 1
        ? "1 Gutschrift-Beleg ohne gültiges Belegdatum fehlt in den Zeiträumen."
        : `${formatInteger(quality.credit_documents_without_valid_date)} Gutschrift-Belege ohne gültiges Belegdatum fehlen in den Zeiträumen.`,
    );
  }
  if ((quality.confirmed_links_with_source_gap ?? 0) > 0) {
    details.push(
      "Mindestens eine bestätigte Zuordnung verweist auf eine fehlende oder nicht aktive Belegquelle.",
    );
  }
  if ((quality.confirmed_links_with_currency_conflict ?? 0) > 0) {
    details.push(
      "Mindestens eine bestätigte Zuordnung hat widersprüchliche Währungsangaben.",
    );
  }
  if (
    (quality.invoice_overallocation_count ?? 0) > 0 ||
    (quality.credit_overallocation_count ?? 0) > 0
  ) {
    details.push(
      "Mindestens eine bestätigte Zuordnung überschreitet den belegbaren Positionsbetrag.",
    );
  }

  return (
    <div
      role="alert"
      className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900"
    >
      <p className="font-semibold">
        Ein Teil der Gutschriften-Daten muss geprüft werden.
      </p>
      <ul className="mt-1 list-disc space-y-1 pl-5 leading-relaxed text-rose-800">
        {details.map((detail) => (
          <li key={detail}>{detail}</li>
        ))}
      </ul>
    </div>
  );
}

function ProposalList({
  fromPath,
  proposals,
  totalCount,
}: {
  fromPath: string;
  proposals: CreditDashboardProposal[];
  totalCount: number;
}) {
  return (
    <section
      id="credit-proposals"
      aria-labelledby="credit-proposals-title"
      className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2
            id="credit-proposals-title"
            className="text-sm font-semibold text-slate-900"
          >
            Zuordnungsvorschläge prüfen
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            {formatInteger(totalCount)} bestätigungspflichtige{" "}
            {totalCount === 1 ? "Zuordnung" : "Zuordnungen"}
          </p>
        </div>
        <Link
          to="/rechnungspruefung?review=credit_expected"
          className="text-sm font-medium text-emerald-700 hover:underline"
        >
          Alle Erwartungen →
        </Link>
      </div>
      <ul className="mt-2 divide-y divide-slate-100">
        {proposals.map((proposal) => (
          <li key={proposal.link_id} className="py-2.5">
            <Link
              to={`/belege/${encodeURIComponent(
                proposal.credit_doc_id,
              )}#gutschrift-zuordnung-${encodeURIComponent(proposal.link_id)}`}
              state={{ from: fromPath }}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-md transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/20"
            >
              <span className="min-w-0 text-sm text-slate-700">
                <span className="font-medium text-slate-900">
                  {proposal.credit_doc_number ?? "Gutschrift ohne Nummer"}
                </span>{" "}
                → {proposal.invoice_doc_number ?? "Rechnung ohne Nummer"}, Pos.{" "}
                {proposal.invoice_line_pos}
                <span className="ml-2 text-xs text-slate-400">
                  {formatDateShort(proposal.credit_doc_date)}
                </span>
              </span>
              <span className="text-sm font-medium tabular-nums text-slate-900">
                {formatMoney(proposal.confirmable_amount, proposal.currency)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {totalCount > proposals.length ? (
        <p className="mt-2 text-xs text-slate-500">
          Weitere Vorschläge finden Sie in den erwarteten Gutschriften.
        </p>
      ) : null}
    </section>
  );
}

function formatInteger(value: number): string {
  return Math.round(value).toLocaleString("de-AT");
}

function validateCustomRange(
  from: string,
  to: string,
  asOf: string,
): string | null {
  if (!from && !to) return null;
  if (!from || !to) return "Bitte wählen Sie Von und Bis.";
  const fromDay = isoDayNumber(from);
  const toDay = isoDayNumber(to);
  const asOfDay = isoDayNumber(asOf);
  if (fromDay === null || toDay === null || asOfDay === null) {
    return "Bitte wählen Sie zwei gültige Datumswerte.";
  }
  if (fromDay > toDay) {
    return "Das Von-Datum darf nicht nach dem Bis-Datum liegen.";
  }
  if (toDay > asOfDay) {
    return "Das Bis-Datum darf nicht in der Zukunft liegen.";
  }
  const inclusiveDays = Math.round((toDay - fromDay) / 86_400_000) + 1;
  if (inclusiveDays > MAX_CUSTOM_PERIOD_DAYS) {
    return "Der eigene Zeitraum darf höchstens 3.660 Tage umfassen.";
  }
  return null;
}

function isoDayNumber(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const parsed = new Date(timestamp);
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null;
  }
  return timestamp;
}

function creditDocumentAmountNote(
  amount: CreditDashboardDocumentAmount,
): string | null {
  if (amount.missing_amount_count === 0) return null;
  return amount.missing_amount_count === 1
    ? "1 Beleg ohne Betrag"
    : `${formatInteger(amount.missing_amount_count)} Belege ohne Betrag`;
}

function formatMoney(
  value: string | number | null,
  currency: string | null,
): string {
  const amount = formatAmount(value);
  if (amount === "–") return amount;
  return `${currency ?? "Währung offen"} ${amount}`;
}

const MONEY_NUMBER_FORMATTER = new Intl.NumberFormat("de-AT", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatAmount(value: string | number | null): string {
  const parsed = toNumber(value);
  if (parsed === null) return "–";
  return MONEY_NUMBER_FORMATTER.format(parsed);
}
